import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import { fmtFechaDisplay, toDateInputValue } from '../../lib/dates.js';
import type { EstadoSolicitud, VacacionesApplication } from './types.js';

const ROLES_RH = ['root', 'head'];

const ESTADO_INFO: Record<EstadoSolicitud, { label: string; tone: BadgeTone }> = {
  pendiente:  { label: 'Pendiente',  tone: 'warning' },
  aprobada:   { label: 'Aprobada',   tone: 'success' },
  rechazada:  { label: 'Rechazada',  tone: 'danger' },
  cancelada:  { label: 'Cancelada',  tone: 'neutral' },
};

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// "Antigüedad del folio" (Prompt BB-5(RH), confirmado con el product owner):
// tiempo transcurrido desde created_at de LA SOLICITUD hasta hoy — no tiene
// relación con la antigüedad del empleado (eso vive en "Años de Servicio").
function fmtAntiguedadFolio(createdAtIso: string): string {
  const dias = Math.max(0, Math.floor((Date.now() - new Date(createdAtIso).getTime()) / 86_400_000));
  if (dias === 0) return 'Hoy';
  if (dias === 1) return '1 día';
  return `${dias} días`;
}

// Fila expandida: overrides de fecha + comentario de rechazo en edición.
interface RevisionState {
  fechaInicio: string;
  fechaFin:    string;
  comentario:  string;
}

// Prompt BB-5(RH) — reemplaza la vieja bandeja de "solo pendientes" por un
// historial completo (cualquier estado), con visibilidad resuelta 100% en
// el backend (ver listarHistorialVisible()): root/head/CEO ven todo; un
// jefe ve las de todo su equipo transitivo bajo el organigrama (no solo
// reportes directos); sin equipo, solo las propias. La acción de aprobar/
// rechazar sigue restringida a quien sea el aprobador_id real de esa fila
// (o root/head) — la visibilidad amplia es de solo lectura para el resto.
export function AprobacionAusenciasPage() {
  const { user } = useAuth();
  const { get, patch } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [expandidoId, setExpandidoId] = useState<string | null>(null);
  const [revision, setRevision] = useState<RevisionState>({ fechaInicio: '', fechaFin: '', comentario: '' });

  const { data: historial, isLoading } = useQuery({
    queryKey: ['vacaciones-applications-historial'],
    queryFn: () => get<VacacionesApplication[]>('/vacaciones-applications/historial'),
  });

  function puedeAccionar(s: VacacionesApplication): boolean {
    return s.estado === 'pendiente' && (s.aprobador_id === user!.sub || ROLES_RH.includes(user!.rol));
  }

  function abrirRevision(s: VacacionesApplication) {
    setExpandidoId(s.id);
    setRevision({
      fechaInicio: toDateInputValue(s.fecha_inicio_solicitada),
      fechaFin:    toDateInputValue(s.fecha_fin_solicitada),
      comentario:  '',
    });
  }

  function cerrarRevision() {
    setExpandidoId(null);
  }

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['vacaciones-applications-historial'] });

  const aprobarMutation = useMutation({
    mutationFn: (id: string) =>
      patch<VacacionesApplication>(`/vacaciones-applications/${id}/aprobar`, {
        fecha_inicio_aprobada: revision.fechaInicio,
        fecha_fin_aprobada:    revision.fechaFin,
      }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Solicitud aprobada');
      cerrarRevision();
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible aprobar la solicitud')),
  });

  const rechazarMutation = useMutation({
    mutationFn: (id: string) =>
      patch<VacacionesApplication>(`/vacaciones-applications/${id}/rechazar`, {
        comentario_aprobador: revision.comentario.trim() || undefined,
      }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Solicitud rechazada');
      cerrarRevision();
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible rechazar la solicitud')),
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Aprobar Ausencias</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Historial de solicitudes de tu equipo</div>
        </div>
        <Button onClick={() => navigate('/ausencias')}>+ Añadir ausencia</Button>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Tipo</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Días</th>
                <th>Estado</th>
                <th>Antigüedad del folio</th>
                <th>ID/Folio</th>
                <th>Comentarios</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!historial || historial.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={10}>
                    {isLoading ? 'Cargando…' : 'No hay solicitudes para mostrar'}
                  </td>
                </tr>
              ) : (
                historial.map(s => (
                  <Fragment key={s.id}>
                    <tr>
                      <td>
                        {s.solicitante_nombre}
                        {s.creado_por_id && s.creado_por_id !== s.usuario_id && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                            Creada por {s.creado_por_nombre}
                          </div>
                        )}
                      </td>
                      <td>{s.tipo_nombre}</td>
                      <td>{fmtFechaDisplay(s.fecha_inicio_aprobada ?? s.fecha_inicio_solicitada)}</td>
                      <td>{fmtFechaDisplay(s.fecha_fin_aprobada ?? s.fecha_fin_solicitada)}</td>
                      <td>{s.dias_habiles_aprobados ?? s.dias_habiles_solicitados}</td>
                      <td>
                        <Badge tone={ESTADO_INFO[s.estado].tone}>{ESTADO_INFO[s.estado].label}</Badge>
                      </td>
                      <td style={{ color: 'var(--muted)' }}>{fmtAntiguedadFolio(s.created_at)}</td>
                      <td style={{ color: 'var(--muted)', fontFamily: 'monospace', fontSize: 12.5 }}>{s.folio}</td>
                      <td style={{ color: 'var(--muted)' }}>{s.comentario_aprobador ?? '—'}</td>
                      <td>
                        {puedeAccionar(s) &&
                          (expandidoId === s.id ? (
                            <Button variant="secondary" size="sm" onClick={cerrarRevision}>Cerrar</Button>
                          ) : (
                            <Button size="sm" onClick={() => abrirRevision(s)}>Revisar</Button>
                          ))}
                      </td>
                    </tr>
                    {expandidoId === s.id && (
                      <tr>
                        <td colSpan={10}>
                          <div className="plat-card" style={{ margin: '4px 0' }}>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                              <div style={{ flex: 1 }}>
                                <Input
                                  label="Desde (aprobado)"
                                  type="date"
                                  value={revision.fechaInicio}
                                  onChange={e => setRevision(r => ({ ...r, fechaInicio: e.target.value }))}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <Input
                                  label="Hasta (aprobado)"
                                  type="date"
                                  value={revision.fechaFin}
                                  onChange={e => setRevision(r => ({ ...r, fechaFin: e.target.value }))}
                                />
                              </div>
                            </div>

                            <div className="field">
                              <label>Comentario (solo si rechazas)</label>
                              <textarea
                                value={revision.comentario}
                                onChange={e => setRevision(r => ({ ...r, comentario: e.target.value }))}
                                rows={2}
                                style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, width: '100%', resize: 'vertical' }}
                              />
                            </div>

                            <div style={{ display: 'flex', gap: 10 }}>
                              <Button
                                onClick={() => aprobarMutation.mutate(s.id)}
                                disabled={aprobarMutation.isPending || rechazarMutation.isPending}
                              >
                                {aprobarMutation.isPending ? 'Aprobando…' : 'Aprobar'}
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() => rechazarMutation.mutate(s.id)}
                                disabled={aprobarMutation.isPending || rechazarMutation.isPending}
                              >
                                {rechazarMutation.isPending ? 'Rechazando…' : 'Rechazar'}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
